import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Code, AlertTriangle, Link } from 'lucide-react';
import { ActionButton } from '@/store/videoFeatures';
import { ButtonTimingCard } from '../shared/ButtonTimingCard';
import { HtmlPreviewCard } from '../shared/HtmlPreviewCard';
import { ensureHttps } from '@/utils/url';

interface CustomHtmlTabProps {
  actionButton: Partial<ActionButton>;
  updateActionButton: (key: string, value: any) => void;
}

export const CustomHtmlTab: React.FC<CustomHtmlTabProps> = ({
  actionButton,
  updateActionButton,
}) => {
  useLanguage();
  const [htmlError, setHtmlError] = useState<string | null>(null);
  const [localHtmlCode, setLocalHtmlCode] = useState(actionButton.html || actionButton.customHtml || '');

  // Sincronizar localHtmlCode quando o dado vem do backend (loadExistingVideo)
  useEffect(() => {
    const backendCode = actionButton.html || actionButton.customHtml || '';
    if (backendCode !== localHtmlCode) {
      setLocalHtmlCode(backendCode);
    }
  }, [actionButton.html, actionButton.customHtml]);

  const validateHtml = (html: string): string | null => {
    if (!html || html.trim() === '') return null;

    // Verificar scripts proibidos
    if (/<script/i.test(html)) {
      return translateInterfaceText("Scripts não são permitidos por segurança.");
    }

    // Verificar event handlers inline (onclick, onerror, etc.)
    if (/on\w+\s*=/i.test(html)) {
      return translateInterfaceText("Event handlers inline não são permitidos.");
    }

    return null;
  };

  const handleHtmlChange = (value: string) => {
    const error = validateHtml(value);
    setHtmlError(error);
    setLocalHtmlCode(value);
    updateActionButton('html', value);
  };

  // Validar HTML inicial
  useEffect(() => {
    if (localHtmlCode) {
      setHtmlError(validateHtml(localHtmlCode));
    }
  }, []);

  const updateTimingProperty = (property: string | Record<string, any>, value?: any) => {
    const currentTiming = actionButton.timing || {};
    const updates = typeof property === 'object' ? property : { [property]: value };
    updateActionButton('timing', { ...currentTiming, ...updates });
  };

  return (
    <div className="space-y-5">
      {/* Card: Link de destino */}
      <Card className="border-border bg-card shadow-sm">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Link className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Link de destino")}</Label>
          </div>
          <Input
            type="url"
            value={actionButton.url || ''}
            onChange={(e) => updateActionButton('url', e.target.value)}
            onBlur={(e) => {
              const urlWithProtocol = ensureHttps(e.target.value);
              if (urlWithProtocol !== e.target.value) {
                updateActionButton('url', urlWithProtocol);
              }
            }}
            placeholder="https://exemplo.com"
            className="h-[44px]"
          />
          <p className="text-xs text-muted-foreground">
            {translateInterfaceText("Para onde o botão HTML vai redirecionar o visitante.")}</p>
        </CardContent>
      </Card>

      {/* Card: Código HTML */}
      <Card className="border-border bg-card shadow-sm">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Código HTML")}</Label>
          </div>

          {/* Campo HTML */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">{translateInterfaceText("Código HTML personalizado")}</Label>
            <Textarea
              value={localHtmlCode}
              onChange={(e) => handleHtmlChange(e.target.value)}
              className={`font-mono text-sm h-[180px] max-h-[180px] resize-none overflow-y-auto bg-muted/50 rounded-xl border-border placeholder:text-muted-foreground placeholder:text-[13px] p-4 ${
                htmlError ? 'border-red-400 focus-visible:ring-red-400' : ''
              }`}
              placeholder={translateInterfaceText("Cole seu código HTML aqui...\nAceita HTML, CSS inline e scripts leves.")}
            />
            
            {/* Validação visual de erro */}
            {htmlError ? (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{htmlError}</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {translateInterfaceText("Aceita HTML e CSS inline. Scripts não são permitidos.")}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tempo de Exibição */}
      <ButtonTimingCard 
        actionButton={actionButton} 
        updateTimingProperty={updateTimingProperty}
        labelPrefix="o HTML"
      />

      {/* Preview do HTML */}
      <HtmlPreviewCard htmlContent={localHtmlCode} />
    </div>
  );
};
